/**
 * Quest framework — canonical data model.
 *
 * A "quest" is an ordered (or wired) set of agent runs that share a
 * descriptor and termination rule. Two kinds ship in v1:
 *
 *   - "loop"  — count-bounded. N pre-rendered cards, same prompt each time,
 *               optional per-card role/model overrides, optional summarizer.
 *   - "ralph" — verify-bounded. Worker → verifier → repeat until verifier
 *               passes (or max-iterations hit). Cards append as iterations run.
 *
 * The data model carries `inputs`/`outputs` wiring on every card so future
 * quest kinds (handoff chain, committee, scheduled triggers) slot in
 * additively. v1 always uses defaults.
 */

import { z } from "zod";
import { AgentProviderSchema } from "../agent/provider-manifest.js";

// ---------------------------------------------------------------------------
// Kind + rules descriptor
// ---------------------------------------------------------------------------

export const QuestKindSchema = z.enum(["loop", "ralph", "run", "orchestrate"]);

/**
 * Per-card edit mode for fields like role, model, prompt:
 *   - "locked"  → field not editable per-card (UI hides it).
 *   - "inherit" → field shows the quest default greyed-out, with an
 *                 explicit "override" affordance.
 *   - "free"    → field always editable, no inheritance UI.
 */
export const FieldModeSchema = z.enum(["locked", "inherit", "free"]);

export const QuestRulesSchema = z.object({
  perCardRole: FieldModeSchema,
  perCardModel: FieldModeSchema,
  perCardPrompt: FieldModeSchema,
  cardsEditableWhileRunning: z.boolean(),
  summarizerCard: z.enum(["off", "optional", "required"]),
  cardExpansion: z.enum(["tool-calls"]),
});

// ---------------------------------------------------------------------------
// Card wiring (future-extensible — v1 always uses defaults)
// ---------------------------------------------------------------------------

export const CardWiringInputsSchema = z.union([
  z.literal("user-prompt"),
  z.literal("previous-card"),
  z.literal("all-previous"),
  z.object({ from: z.array(z.string()) }),
]);

export const CardWiringOutputsSchema = z.union([
  z.literal("next-card"),
  z.literal("summarizer"),
  z.literal("none"),
  z.object({ to: z.array(z.string()) }),
]);

export const CardWiringSchema = z.object({
  inputs: CardWiringInputsSchema,
  outputs: CardWiringOutputsSchema,
});

// ---------------------------------------------------------------------------
// Card record
// ---------------------------------------------------------------------------

export const QuestCardStatusSchema = z.enum([
  "queued",
  "running",
  "succeeded",
  "failed",
  "stopped",
]);

/**
 * Outcome metadata specific to ralph cards (worker + verifier pairing).
 * For "loop" cards this is null.
 */
export const QuestCardVerifySchema = z.object({
  verifierAgentId: z.string().nullable(),
  passed: z.boolean().nullable(),
  reason: z.string().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
});

export const QuestCardSchema = z.object({
  /**
   * 1..N for iteration cards. The summarizer card uses 0 as a sentinel —
   * it lives on `QuestRecord.summarizerCard`, not in the cards array,
   * and the UI renders it as "Summary" when it sees index === 0.
   */
  index: z.number().int().nonnegative(),
  /** Role name resolved against the agents/.scanner. Null = use quest default. */
  role: z.string().nullable(),
  /** Model override. Null = use quest.defaultModel. */
  model: z.string().nullable(),
  /** Prompt override. Null = use quest.prompt. */
  prompt: z.string().nullable(),
  wiring: CardWiringSchema,
  status: QuestCardStatusSchema,
  /** Populated when the card runs. Null until the runner spawns the agent. */
  agentId: z.string().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  /** Ralph-only — null for loop cards. */
  verify: QuestCardVerifySchema.nullable(),
});

// ---------------------------------------------------------------------------
// Termination rules (discriminated union by `type`)
// ---------------------------------------------------------------------------

export const QuestTerminationCountSchema = z.object({
  type: z.literal("count"),
  /** 1..10 in v1. */
  n: z.number().int().min(1).max(10),
});

export const QuestTerminationVerifySchema = z.object({
  type: z.literal("verify"),
  /** Verifier agent system prompt. Null means rely on shell checks alone. */
  verifyPrompt: z.string().nullable(),
  /** Shell commands that must exit 0 for the iteration to pass. */
  verifyChecks: z.array(z.string()),
  /**
   * Exact completion token that MUST appear in worker output before the quest
   * may succeed. Mirrors Ralph's completion-promise guardrail.
   */
  completionPromise: z.string().trim().min(1).optional().default("<promise>COMPLETE</promise>"),
  /** Hard cap on iterations regardless of verifier outcome. */
  maxIterations: z.number().int().min(1),
});

/**
 * Orchestrate termination — plan-bounded. The queen runs ONCE with the user
 * prompt + role roster and emits a deterministic plan: an ordered list of
 * waves, where each wave is a set of role dispatches that run in parallel.
 * The runner executes the plan exactly as written (no re-querying, no loop).
 * Termination is implicit — when the last wave finishes, the quest succeeds.
 */
export const QuestTerminationOrchestrateSchema = z.object({
  type: z.literal("orchestrate"),
});

export const QuestTerminationSchema = z.discriminatedUnion("type", [
  QuestTerminationCountSchema,
  QuestTerminationVerifySchema,
  QuestTerminationOrchestrateSchema,
]);

// ---------------------------------------------------------------------------
// Quest record
// ---------------------------------------------------------------------------

export const QuestStatusSchema = z.enum(["running", "succeeded", "failed", "stopped"]);

export const QuestRecordSchema = z.object({
  id: z.string(),
  kind: QuestKindSchema,
  rules: QuestRulesSchema,
  cwd: z.string(),
  /** User's task prompt — fed to every card unless overridden per-card. */
  prompt: z.string(),
  /**
   * Short auto-generated label for the quest, derived from the user prompt by a
   * small internal metadata agent (mirrors how single-agent sessions get a
   * title). Empty string until generation finishes (or if it fails). Optional +
   * default so old persisted records remain valid.
   */
  name: z.string().optional().default(""),
  defaultProvider: AgentProviderSchema,
  defaultModel: z.string().nullable(),
  termination: QuestTerminationSchema,
  /**
   * For "loop" kind: pre-rendered N cards from the start.
   * For "ralph" kind: appended as iterations execute.
   */
  cards: z.array(QuestCardSchema),
  /** Populated when summarizer is enabled. Runs after all cards complete. */
  summarizerCard: QuestCardSchema.nullable(),
  /**
   * Queen-bee coordinator card for "orchestrate" kind. The queen reads the
   * hivemind doc, decides dispatch waves, and writes JSON decisions back. It
   * is promotable (same affordance as regular cards). Null for non-orchestrate
   * kinds. Optional + default so old persisted records remain valid.
   */
  queenCard: QuestCardSchema.nullable().optional().default(null),
  /**
   * Path to the orchestration's hivemind markdown doc. Null for non-
   * orchestrate kinds. Optional + default so old persisted records remain
   * valid.
   */
  hivemindPath: z.string().nullable().optional().default(null),
  status: QuestStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  stopRequestedAt: z.string().nullable(),
  /**
   * auth-server user-id of the session that created this quest. Required
   * (non-null) on the write side — the single-tenant fallback has been
   * removed. Legacy null-owner records on disk are stamped to the host
   * owner's userId by the boot-time `stampLegacyOwnerIds` migration BEFORE
   * QuestService loads, so this schema parses cleanly on every supported
   * boot.
   */
  ownerUserId: z.string(),
  /**
   * Display name of the owner. Optional — older records may not carry it
   * and the consumer falls back gracefully.
   */
  ownerUsername: z.string().nullable().optional().default(null),
});

export type QuestKind = z.infer<typeof QuestKindSchema>;
export type FieldMode = z.infer<typeof FieldModeSchema>;
export type QuestRules = z.infer<typeof QuestRulesSchema>;
export type CardWiringInputs = z.infer<typeof CardWiringInputsSchema>;
export type CardWiringOutputs = z.infer<typeof CardWiringOutputsSchema>;
export type CardWiring = z.infer<typeof CardWiringSchema>;
export type QuestCardStatus = z.infer<typeof QuestCardStatusSchema>;
export type QuestCardVerify = z.infer<typeof QuestCardVerifySchema>;
export type QuestCard = z.infer<typeof QuestCardSchema>;
export type QuestTerminationCount = z.infer<typeof QuestTerminationCountSchema>;
export type QuestTerminationVerify = z.infer<typeof QuestTerminationVerifySchema>;
export type QuestTerminationOrchestrate = z.infer<typeof QuestTerminationOrchestrateSchema>;
export type QuestTermination = z.infer<typeof QuestTerminationSchema>;
export type QuestStatus = z.infer<typeof QuestStatusSchema>;
export type QuestRecord = z.infer<typeof QuestRecordSchema>;
